"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { IHeader } from "@/sanity/lib/definitions";
import { cn } from "@/lib/utils";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useGlobalPathname } from "./HeaderContext";
import { CTAButton } from "@/shadcn/ui/button";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { resolvePageLink } from "@/sanity/globals/CTA";
import TSLogo from "@/components/global/ui/TSLogo";

if (typeof window !== "undefined") {
  gsap.registerPlugin(useGSAP);
}

const IconMobileMenuClosed = () => (
  <svg
    width="24"
    height="14"
    viewBox="0 0 24 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="0" y="2" width="24" height="2" fill="white" />
    <rect x="0" y="10" width="24" height="2" fill="white" />
  </svg>
);

const IconMobileMenuOpen = () => (
  <svg
    className="flex-shrink-0 w-[2rem] h-[2rem]"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0"
      y="11"
      width="24"
      height="2"
      fill="currentColor"
      transform="rotate(-45 12 12)"
    />
    <rect
      x="0"
      y="11"
      width="24"
      height="2"
      fill="currentColor"
      transform="rotate(45 12 12)"
    />
  </svg>
);

function normalizePath(path: string): string {
  const trimmed = path.replace(/\/+$/, "") || "/";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function checkIsActive(
  currentPathname: string,
  currentHash: string,
  link: string,
): boolean {
  const hasHash = link.includes("#");
  const [linkPath, linkHash] = link.split("#");
  const normalizedLinkPath = normalizePath(linkPath || "/");
  const normalizedCurrentPath = normalizePath(currentPathname);

  if (!hasHash) {
    return normalizedCurrentPath === normalizedLinkPath;
  }

  return (
    normalizedCurrentPath === normalizedLinkPath &&
    currentHash === `#${linkHash}`
  );
}

export default function Header({
  data,
  headerType = "main",
  className,
}: {
  data: IHeader;
  headerStyle?: "dark" | "light";
  headerType?: "main" | "lp" | "hidden";
  ctaLoginButtonType?: "default" | "green" | "gray" | "transparent" | "login";
  ctaButtonType?: "default" | "green" | "gray" | "transparent" | "login";
  showBorder?: boolean;
  className?: string;
}) {
  const containerRef = useRef<HTMLElement>(null);
  const menuId = useId();
  const pathnames = useGlobalPathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeHash, setActiveHash] = useState("");

  useEffect(() => {
    const desktop = matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => {
      if (desktop.matches) setIsMobileMenuOpen(false);
    };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!data?.menu) return;

    const anchorIds = data.menu
      .map((item) => {
        const link = resolvePageLink(item.link ?? null) ?? "/";
        if (!link.includes("#")) return null;
        return link.split("#")[1];
      })
      .filter(Boolean) as string[];

    if (anchorIds.length === 0) return;

    const updateHash = () => setActiveHash(window.location.hash);
    window.addEventListener("hashchange", updateHash);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          setActiveHash(`#${visible[0].target.id}`);
        }
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    anchorIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    updateHash();

    return () => {
      observer.disconnect();
      window.removeEventListener("hashchange", updateHash);
    };
  }, [data?.menu]);

  useGSAP(
    () => {
      gsap.set(containerRef.current, { visibility: "visible" });
      if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const navbarTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.5 },
      });
      navbarTL.from(containerRef.current, { y: "-0.5rem", delay: 1 });
    },
    { scope: containerRef },
  );

  if (!data) return null;

  return (
    <DialogPrimitive.Root
      open={isMobileMenuOpen}
      onOpenChange={setIsMobileMenuOpen}
    >
      {/* Blend on the outer wrapper so the logo includes the page backdrop. */}
      <div
        className="hidden absolute inset-0 pointer-events-none lg:block"
        style={{ zIndex: 16, mixBlendMode: "exclusion" }}
      >
        <div className="sticky w-full top-[1rem]">
          <div className="px-[7rem] py-[1rem]">
            <Link
              href="/"
              aria-label="TradeStats home"
              className="inline-block pointer-events-auto"
            >
              <TSLogo />
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute inset-0 pointer-events-none z-[15]">
        <header
          id="navbar"
          ref={containerRef}
          className={cn("sticky top-0 w-full pointer-events-auto ", className)}
        >
          <div
            data-header-desktop
            className="hidden justify-between items-center lg:flex px-[7rem] py-[1rem]"
          >
            {/* Reserve layout space for the separately layered logo. */}
            <div className="w-[3.75rem] h-[4rem]" aria-hidden />

            {headerType === "main" && (
              <div
                data-header-nav
                className="flex items-center gap-[7rem] p-[0.5rem] rounded-[1rem] backdrop-blur-[1rem] bg-[#020100]"
              >
                <div className="flex items-center">
                  {data.menu?.map((item, index) => {
                    const link = resolvePageLink(item.link ?? null) ?? "/";
                    const isActive = checkIsActive(
                      pathnames.currentPathname,
                      activeHash,
                      link,
                    );

                    return (
                      <Link
                        key={index}
                        href={link}
                        target={item.link?.externalLink ? "_blank" : "_self"}
                        className={cn(
                          "px-[2rem] py-[1.5rem] rounded-[1rem] font-spacemono text-[max(12px,1.75rem)] uppercase tracking-[0.07rem] leading-normal whitespace-nowrap transition-colors duration-200",
                          isActive
                            ? "text-white"
                            : "text-white/50 hover:text-white",
                        )}
                      >
                        {item.title}
                      </Link>
                    );
                  })}
                </div>

                <div className="flex items-center pr-[.5rem]">
                  {data?.headerLoginCTA && (
                    <CTAWrapper
                      cta={data.headerLoginCTA.cta}
                      className="leading-normal text-white uppercase whitespace-nowrap transition-colors duration-200 px-[2rem] py-[1.5rem] rounded-[1rem] font-spacemono text-[max(12px,1.75rem)] tracking-[0.07rem] hover:text-white/80"
                    >
                      {data.headerLoginCTA.cta?.title ?? "Sign In"}
                    </CTAWrapper>
                  )}
                  {data?.headerCTA && (
                    <CTAButton
                      animateText={false}
                      className="min-w-0 [&_.button]:!h-[4.25rem] [&_.button]:!px-[2rem] [&_.button]:sm:!px-[2rem] [&_.button]:!py-[0] [&_.button]:!text-[max(12px,1.75rem)] [&_.button]:!rounded-[0.5rem] [&_.button]:!font-helveticaNeue [&_.button]:!tracking-normal"
                      data={data.headerCTA}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <div
            data-header-mobile
            className="flex lg:hidden px-[2rem] pt-[1rem]"
          >
            <div
              data-header-mobile-bar
              className="grid grid-cols-3 items-center w-full py-[1.5rem] px-[1.25rem] rounded-[1.2rem] backdrop-blur-[1rem]"
              style={{ background: "rgba(0,0,0,0.7)" }}
            >
              <div className="flex justify-start">
                {headerType === "main" ? (
                  <DialogPrimitive.Trigger asChild>
                    <button
                      type="button"
                      aria-label="Open menu"
                      aria-controls={menuId}
                      className="flex items-center text-white whitespace-nowrap gap-[0.8rem] px-[1.5rem] h-[4.2rem] rounded-[0.6rem]"
                      style={{ background: "rgba(255,255,255,0.05)" }}
                    >
                      <IconMobileMenuClosed />
                      <span className="font-medium font-helveticaNeue text-[1.8rem] leading-[1.4]">
                        Menu
                      </span>
                    </button>
                  </DialogPrimitive.Trigger>
                ) : (
                  <div />
                )}
              </div>

              <div className="flex justify-center">
                <Link href="/" aria-label="TradeStats home">
                  <TSLogo />
                </Link>
              </div>

              <div className="flex justify-end">
                {data?.headerCTA?.cta && headerType === "main" ? (
                  <CTAWrapper
                    cta={data.headerCTA.cta}
                    className="flex items-center justify-center h-[4.2rem] px-[2rem] rounded-[0.6rem] bg-[#ff5500] hover:bg-[#e64d00] text-black font-helveticaNeue text-[1.8rem] font-medium leading-[80%] whitespace-nowrap transition-colors duration-200"
                  >
                    {data.headerCTA.cta?.title ?? "Start Now"}
                  </CTAWrapper>
                ) : (
                  <div />
                )}
              </div>
            </div>
          </div>

          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 lg:hidden z-[80] bg-black/30 backdrop-blur-[2rem] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 duration-300 motion-reduce:duration-0" />
            <DialogPrimitive.Content
              id={menuId}
              aria-describedby={undefined}
              className="fixed top-0 left-0 right-0 lg:hidden z-[81] max-h-[100dvh] overflow-y-auto bg-white rounded-[0.6rem] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-top-full data-[state=closed]:slide-out-to-top-full duration-300 motion-reduce:duration-0"
            >
              <DialogPrimitive.Title className="sr-only">
                Navigation
              </DialogPrimitive.Title>
              <div className="grid grid-cols-3 items-center p-[2rem]">
                <div className="flex justify-start">
                  <DialogPrimitive.Close asChild>
                    <button
                      type="button"
                      aria-label="Close menu"
                      className="flex items-center text-black whitespace-nowrap gap-[1.2rem] px-[1rem] py-[0.4rem] h-[4.2rem] rounded-[0.6rem] bg-black/10"
                    >
                      <IconMobileMenuOpen />
                      <span className="font-medium font-helveticaNeue text-[1.8rem] leading-[1.4]">
                        Menu
                      </span>
                    </button>
                  </DialogPrimitive.Close>
                </div>

                <div className="flex justify-center">
                  <Link
                    href="/"
                    aria-label="TradeStats home"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <TSLogo fill="black" width="3rem" height="3.2rem" />
                  </Link>
                </div>

                <div className="flex justify-end">
                  {data?.headerCTA?.cta && (
                    <CTAWrapper
                      cta={data.headerCTA.cta}
                      className="flex items-center justify-center h-[4.2rem] px-[1.6rem] rounded-[0.6rem] bg-[#ff5500] hover:bg-[#e64d00] text-black font-helveticaNeue text-[1.8rem] font-medium leading-[80%] whitespace-nowrap transition-colors duration-200"
                    >
                      {data.headerCTA.cta?.title ?? "Start Now"}
                    </CTAWrapper>
                  )}
                </div>
              </div>

              <div className="h-px bg-black/10" />

              <div className="flex flex-col px-[2rem]">
                {data.menu?.map((item, index) => {
                  const link = resolvePageLink(item.link ?? null) ?? "/";
                  const isActive = checkIsActive(
                    pathnames.currentPathname,
                    activeHash,
                    link,
                  );

                  return (
                    <Link
                      key={index}
                      href={link}
                      target={item.link?.externalLink ? "_blank" : "_self"}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center py-[2rem] w-full font-helveticaNeue text-[2.0rem] border-b border-black/10 last:border-0 capitalize transition-colors duration-150",
                        isActive
                          ? "text-black"
                          : "text-black/50 hover:text-black",
                      )}
                    >
                      {item.title}
                    </Link>
                  );
                })}
              </div>

              <div className="flex flex-col gap-[0.8rem] px-[2rem] pt-[2rem] pb-[2rem]">
                {data?.headerCTA?.cta && (
                  <CTAWrapper
                    cta={data.headerCTA.cta}
                    className="flex justify-center items-center w-full font-medium text-black transition-colors duration-200 h-[6rem] rounded-[1rem] bg-[#ff5500] font-helveticaNeue text-[1.8rem] hover:bg-[#e64d00]"
                  >
                    {data.headerCTA.cta?.title ?? "Start Now"}
                  </CTAWrapper>
                )}
                {data?.headerLoginCTA?.cta && (
                  <CTAWrapper
                    cta={data.headerLoginCTA.cta}
                    className="flex justify-center items-center w-full font-medium text-black border transition-colors duration-200 h-[6rem] rounded-[1rem] border-black/50 font-helveticaNeue text-[1.8rem] hover:bg-black/5"
                  >
                    {data.headerLoginCTA.cta?.title ?? "Sign In"}
                  </CTAWrapper>
                )}
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </header>
      </div>
    </DialogPrimitive.Root>
  );
}
